/**
 * @fileoverview Authentication services for Okta integration.
 *
 * This module provides a centralized authentication service using Okta's OAuth 2.0/OIDC
 * implementation. It includes enhanced error reporting, user profile management, and
 * authentication flow utilities for the lender quotes application.
 *
 * Key features:
 * - Okta sign-in/sign-out with redirect flows
 * - User profile retrieval and management
 * - Comprehensive error tracking and reporting
 * - Username extraction for failed authentication attempts
 * - Environment-aware configuration
 *
 * @requires @okta/okta-auth-js - Okta JavaScript SDK
 * @requires @/lib/errorService - Centralized error reporting service
 */

import { errorService } from '@/lib/errorService';
import { OktaAuth } from '@okta/okta-auth-js';
import type { AuthErrorContext } from './types';

/**
 * Authentication redirect path constants.
 * Defines the standard paths used for Okta authentication flows.
 */
const AUTH_PATHS = {
  /** Path for handling Okta login callback after successful authentication */
  LOGIN_CALLBACK: '/login/callback',
  /** Path for logout confirmation page */
  LOGOUT: '/logout',
} as const;

/**
 * Get the current origin of the window.
 * Used to construct redirect URIs for Okta authentication flows.
 *
 * @returns The current origin of the window (e.g., 'https://example.com')
 * @throws {Error} If window is not defined (server-side rendering)
 *
 */
const getCurrentOrigin = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  throw new Error('Window is not defined');
};

/**
 * Configured Okta authentication instance.
 * Handles OAuth 2.0/OIDC authentication flows with the following configuration:
 * - Uses environment-specific issuer and client ID
 * - Redirects to login callback path after authentication
 * - Requests standard OIDC scopes for user profile information
 */
const oktaAuth = new OktaAuth({
  issuer: import.meta.env.VITE_OKTA_ISSUER,
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID,
  redirectUri: `${getCurrentOrigin()}${AUTH_PATHS.LOGIN_CALLBACK}`,
  scopes: ['openid', 'profile', 'email'],
});

/**
 * Initiates Okta sign-in redirect flow with enhanced error reporting.
 * Redirects user to Okta login page and handles authentication errors.
 *
 * @param originalUri - Optional URI to redirect to after successful login
 * @param username - Optional attempted username for error context
 *
 * @throws {Error} Authentication errors from Okta SDK
 *
 * @example
 * ```typescript
 * // Basic login redirect
 * await signInWithRedirect();
 *
 * // Login with return URL
 * await signInWithRedirect('/dashboard');
 *
 * // Login with username context for error tracking
 * await signInWithRedirect('/dashboard', 'user@example.com');
 * ```
 */
export async function signInWithRedirect(
  originalUri?: string,
  username?: string
) {
  try {
    await oktaAuth.signInWithRedirect({ originalUri });
  } catch (error) {
    const authContext: AuthErrorContext = {
      operation: 'login',
      attempted_username: username,
      auth_state: {
        isAuthenticated: await oktaAuth.isAuthenticated(),
        isPending: Boolean(oktaAuth.authStateManager.getAuthState()?.isPending),
      },
      additional_context: {
        originalUri,
        currentUrl:
          typeof window !== 'undefined' ? window.location.href : undefined,
      },
    };

    errorService.reportAuthError(error, authContext);
    throw error;
  }
}

/**
 * Initiates Okta sign-out flow with enhanced error reporting.
 * Clears user session and redirects to specified logout URL.
 *
 * @param postLogoutRedirectUri - Optional URI to redirect to after logout
 *
 * @throws {Error} Authentication errors from Okta SDK
 *
 * @example
 * ```typescript
 * // Basic logout
 * await signOut();
 *
 * // Logout with custom redirect
 * await signOut('https://company.com/goodbye');
 * ```
 */
export async function signOut(postLogoutRedirectUri?: string) {
  try {
    await oktaAuth.signOut({ postLogoutRedirectUri });
  } catch (error) {
    const authContext: AuthErrorContext = {
      operation: 'logout',
      auth_state: {
        isAuthenticated: await oktaAuth.isAuthenticated(),
        isPending: Boolean(oktaAuth.authStateManager.getAuthState()?.isPending),
      },
      additional_context: {
        postLogoutRedirectUri,
        currentUrl:
          typeof window !== 'undefined' ? window.location.href : undefined,
      },
    };

    errorService.reportAuthError(error, authContext);
    throw error;
  }
}

/**
 * Retrieves the current user's profile information from Okta.
 * Returns user details including name, email, and other profile data.
 *
 * @returns Promise that resolves to user profile object from Okta
 * @throws {Error} If user is not authenticated or profile fetch fails
 */
export async function getUserProfile() {
  try {
    return await oktaAuth.getUser();
  } catch (error) {
    const authContext: AuthErrorContext = {
      operation: 'get_user_profile',
      auth_state: {
        isAuthenticated: await oktaAuth.isAuthenticated(),
        isPending: Boolean(oktaAuth.authStateManager.getAuthState()?.isPending),
      },
    };

    errorService.reportAuthError(error, authContext);
    throw error;
  }
}

/**
 * Extract attempted username from various authentication error contexts.
 * Attempts to parse username from URL parameters, form data, or error messages
 * to provide better error context for debugging failed login attempts.
 *
 * @param error - The authentication error object
 * @param context - Optional context containing URL or form data
 * @param context.url - URL that may contain username parameters
 * @param context.formData - Form data that may contain username/email
 *
 * @returns Extracted username if found, undefined otherwise
 *
 * @example
 * ```typescript
 * // Extract from URL parameters
 * const username = extractAttemptedUsername(error, {
 *   url: 'https://app.com/login?login_hint=user@example.com'
 * }); // returns 'user@example.com'
 *
 * // Extract from form data
 * const username = extractAttemptedUsername(error, {
 *   formData: { username: 'john.doe', password: '***' }
 * }); // returns 'john.doe'
 *
 * // Extract from error message
 * const username = extractAttemptedUsername(
 *   new Error('Authentication failed for user: alice@company.com')
 * ); // returns 'alice@company.com'
 * ```
 */
export function extractAttemptedUsername(
  error: unknown,
  context?: { url?: string; formData?: Record<string, unknown> }
): string | undefined {
  // Try to extract from URL parameters (common in OAuth flows)
  if (context?.url) {
    try {
      const urlParams = new URL(context.url).searchParams;
      return (
        urlParams.get('login_hint') || urlParams.get('username') || undefined
      );
    } catch {
      // Invalid URL, continue
    }
  }

  // Try to extract from form data
  if (context?.formData) {
    const username = context.formData.username || context.formData.email;
    if (typeof username === 'string') {
      return username;
    }
  }

  // Try to extract from error message (some auth errors include username)
  if (error instanceof Error) {
    const usernameMatch = error.message.match(/user[:\s]+([^\s,]+)/i);
    if (usernameMatch?.[1]) {
      return usernameMatch[1];
    }
  }

  return undefined;
}

/**
 * Default export: Configured Okta authentication instance.
 * Use this for direct access to Okta SDK methods not wrapped by this service.
 */
export default oktaAuth;

/**
 * Named exports for authentication utilities.
 * - AUTH_PATHS: Standard authentication redirect paths
 * - getCurrentOrigin: Helper for getting current window origin
 */
export { AUTH_PATHS, getCurrentOrigin };
